import { useState, useMemo } from "react";

function calcMonthlyPayment(principal, annualRate, months) {
  if (annualRate === 0) return principal / months;
  const r = annualRate / 100 / 12;
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

function calcAmortization(principal, annualRate, months) {
  const r = annualRate / 100 / 12;
  const payment = calcMonthlyPayment(principal, annualRate, months);
  let balance = principal;
  const schedule = [];
  for (let i = 0; i < months; i++) {
    const interest = balance * r;
    const principalPaid = payment - interest;
    balance = Math.max(0, balance - principalPaid);
    schedule.push(balance);
  }
  return schedule;
}

// Depreciation rates updated to reflect real-world market data.
// Year 1 loss includes the immediate drive-off-the-lot hit plus first-year market depreciation.
// Annual rate applies from year 2 onward, compounding each year.
const DEPR_PROFILES = {
  strong:  { yr1: 0.15, annual: 0.10, label: "Holds value well",    examples: "Toyota Tacoma, Honda CR-V, Subaru Outback, Jeep Wrangler, most trucks" },
  average: { yr1: 0.22, annual: 0.15, label: "Average depreciation", examples: "Honda Accord, Toyota Camry, Nissan Rogue, typical crossovers/SUVs" },
  fast:    { yr1: 0.35, annual: 0.22, label: "Depreciates fast",     examples: "BMW, Mercedes, Cadillac, Chrysler, Chevy Malibu, Volvo, Lincoln, most EVs (except Tesla Model Y), minivans" },
};

function calcDepreciationSchedule(carPrice, months, profile = "average", isNew = true) {
  const { yr1, annual } = DEPR_PROFILES[profile];
  const retainAfterYr1 = 1 - yr1;
  const schedule = [];
  for (let i = 0; i < months; i++) {
    const year = i / 12;
    let value;
    if (isNew) {
      // New car: steep year-1 cliff then annual rate
      if (year <= 1) value = carPrice * (1 - yr1 * year);
      else value = carPrice * retainAfterYr1 * Math.pow(1 - annual, year - 1);
    } else {
      // Used car: previous owner absorbed year-1 hit — start at gentler annual rate immediately
      value = carPrice * Math.pow(1 - annual, year);
    }
    schedule.push(Math.max(0, value));
  }
  return schedule;
}

// Benchmarks updated March 2026. Revisit every 6 months or after Fed rate changes.
// Bank/CU rates: typical rates from banks and credit unions at current market rates.
// Manufacturer incentive rates: what captive finance arms (Toyota Financial, Ford Motor Credit etc.)
// offer on new vehicles to qualified buyers — not available on used cars.
const CREDIT_RATE_BENCHMARKS = {
  bank:         { excellent: 5.5,  good: 7.5,  fair: 11.0, poor: 16.0 },
  manufacturer: { excellent: 2.9,  good: 4.9,  fair: 7.9,  poor: null  },
};

function estimateTaxRate(income) {
  if (income < 30000) return 0.12;
  if (income < 60000) return 0.18;
  if (income < 100000) return 0.22;
  if (income < 150000) return 0.26;
  return 0.30;
}

const C = {
  green: "#22c55e", yellow: "#f59e0b", red: "#ef4444",
  blue: "#3b82f6", purple: "#8b5cf6", orange: "#f97316",
  bg: "#0f1117", card: "#1a1d27", border: "#2a2d3a",
  text: "#e2e8f0", muted: "#64748b",
};

function EquityChart({ loanSchedule, deprSchedule, months }) {
  if (!loanSchedule.length) return null;
  const w = 400, h = 140;
  const maxVal = Math.max(...deprSchedule.slice(0, months), ...loanSchedule);
  const toY = v => h - 8 - (v / maxVal) * (h - 16);
  const loanPts = loanSchedule.map((v, i) => `${(i / (months - 1)) * w},${toY(v)}`).join(" ");
  const deprPts = deprSchedule.slice(0, months).map((v, i) => `${(i / (months - 1)) * w},${toY(v)}`).join(" ");
  let crossover = null;
  for (let i = 0; i < months; i++) {
    if (deprSchedule[i] >= loanSchedule[i]) { crossover = i; break; }
  }
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
      <defs>
        <linearGradient id="uwG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ef4444" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
        </linearGradient>
      </defs>
      {crossover !== null && <rect x="0" y="0" width={(crossover / (months - 1)) * w} height={h} fill="url(#uwG)" />}
      <polyline points={deprPts} fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5 3" strokeLinejoin="round" />
      <polyline points={loanPts} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />
      {crossover !== null && <>
        <line x1={(crossover / (months - 1)) * w} y1="0" x2={(crossover / (months - 1)) * w} y2={h} stroke="#22c55e" strokeWidth="1" strokeDasharray="3 3" />
        <circle cx={(crossover / (months - 1)) * w} cy={toY(loanSchedule[crossover])} r="4" fill="#22c55e" />
      </>}
    </svg>
  );
}

function OppCostChart({ monthlyDiff, months }) {
  if (monthlyDiff <= 0) return null;
  const pts = [];
  let bal = 0;
  const r = 0.07 / 12;
  for (let i = 0; i <= months; i++) { bal = bal * (1 + r) + monthlyDiff; pts.push(bal); }
  const max = pts[pts.length - 1];
  const w = 400, h = 85;
  const poly = pts.map((v, i) => `${(i / months) * w},${h - 4 - (v / max) * (h - 8)}`).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
      <defs>
        <linearGradient id="opG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${poly} ${w},${h}`} fill="url(#opG)" />
      <polyline points={poly} fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

function PrincipalVsInterestBar({ principal, totalInterest, totalPaid }) {
  if (!totalPaid) return null;
  const pPct = (principal / totalPaid) * 100;
  const iPct = (totalInterest / totalPaid) * 100;
  const iColor = iPct > 40 ? "#ef4444" : iPct > 25 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", height: 20, borderRadius: 6, overflow: "hidden", gap: 2 }}>
        <div style={{ width: `${pPct}%`, background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {pPct > 20 && <span style={{ fontSize: 16, color: "#fff", fontWeight: 700, whiteSpace: "nowrap", padding: "0 4px" }}>Principal</span>}
        </div>
        <div style={{ width: `${iPct}%`, background: iColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {iPct > 12 && <span style={{ fontSize: 16, color: "#fff", fontWeight: 700, whiteSpace: "nowrap", padding: "0 4px" }}>Interest</span>}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
        <div style={{ flex: 1, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 7, padding: "7px 10px" }}>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 2 }}>Principal financed</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#3b82f6", letterSpacing: "-0.02em" }}>{new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(principal)}</div>
          <div style={{ fontSize: 16, color: "#64748b", marginTop: 1 }}>{pPct.toFixed(0)}% of total outlay</div>
        </div>
        <div style={{ flex: 1, background: `rgba(${iPct > 40 ? "239,68,68" : iPct > 25 ? "245,158,11" : "34,197,94"},0.08)`, border: `1px solid rgba(${iPct > 40 ? "239,68,68" : iPct > 25 ? "245,158,11" : "34,197,94"},0.2)`, borderRadius: 7, padding: "7px 10px" }}>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 2 }}>Total interest paid</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: iColor, letterSpacing: "-0.02em" }}>{new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(totalInterest)}</div>
          <div style={{ fontSize: 16, color: "#64748b", marginTop: 1 }}>{iPct.toFixed(0)}% of total outlay</div>
        </div>
        <div style={{ flex: 1, background: "rgba(100,116,139,0.08)", border: "1px solid rgba(100,116,139,0.2)", borderRadius: 7, padding: "7px 10px" }}>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 2 }}>Total outlay</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#e2e8f0", letterSpacing: "-0.02em" }}>{new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(totalPaid)}</div>
          <div style={{ fontSize: 16, color: "#64748b", marginTop: 1 }}>inc. down payment</div>
        </div>
      </div>
    </div>
  );
}

function CashFlowBar({ leftover, takeHome }) {
  if (!takeHome) return null;
  const pct = Math.max(0, Math.min(100, (leftover / takeHome) * 100));
  const color = leftover < 0 ? C.red : leftover < 500 ? C.yellow : C.green;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ height: 7, background: C.border, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 4, transition: "width 0.4s" }} />
      </div>
      <div style={{ fontSize: 16, color: C.muted, marginTop: 3 }}>
        {leftover >= 0 ? `${pct.toFixed(0)}% of take-home remaining after all obligations` : "Spending exceeds take-home — effectively in the red every month"}
      </div>
    </div>
  );
}

const fmt = n => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const pct = n => `${n.toFixed(1)}%`;

export default function App() {
  const [fin, setFin] = useState({ annualIncome: "", monthlyLiving: "", monthlyDebt: "", savings: "", credit: "good", financingType: "bank", currentInsurance: "", newInsurance: "" });
  const [car, setCar] = useState({ price: "", down: "", tradeIn: "", tradeInPayoff: "", apr: "", term: "60", isNew: true, deprProfile: "average" });
  const sf = (k, v) => setFin(p => ({ ...p, [k]: v }));
  const sc = (k, v) => setCar(p => ({ ...p, [k]: v }));

  const R = useMemo(() => {
    const income = +fin.annualIncome || 0;
    const living = +fin.monthlyLiving || 0;
    const debt = +fin.monthlyDebt || 0;
    const currentInsurance = +fin.currentInsurance || 0;
    const newInsurance     = +fin.newInsurance || 0;
    // Insurance delta: how much more (or less) per month the new car costs to insure
    const insuranceDelta = newInsurance - currentInsurance;
    const hasInsuranceData = newInsurance > 0;
    const savings = +fin.savings || 0;
    const price = +car.price || 0;
    const down = +car.down || 0;
    const tradeIn = +car.tradeIn || 0;
    const benchmarkTable = CREDIT_RATE_BENCHMARKS[fin.financingType] || CREDIT_RATE_BENCHMARKS.bank;
    const benchmarkRaw = benchmarkTable[fin.credit];
    const apr = +car.apr || benchmarkRaw || CREDIT_RATE_BENCHMARKS.bank[fin.credit];
    const months = +car.term || 60;
    if (!income || !price) return null;

    const grossMo = income / 12;
    const taxRate = estimateTaxRate(income);
    const takehome = grossMo * (1 - taxRate);
    // ── Trade-in equity calculation ────────────────────────────────────────
    const tradeInPayoff = +car.tradeInPayoff || 0;
    const tradeInEquity = tradeIn - tradeInPayoff;        // negative = rolled debt
    const rolledDebt    = tradeInEquity < 0 ? Math.abs(tradeInEquity) : 0;
    const tradeInCredit = tradeInEquity > 0 ? tradeInEquity : 0;

    // Principal: price minus cash down minus positive equity, PLUS any rolled negative equity
    const principal = Math.max(0, price - down - tradeInCredit + rolledDebt);
    const payment   = calcMonthlyPayment(principal, apr, months);
    const totalPaid = payment * months + down;
    const totalInterest = (payment * months) - principal; // interest = payments minus principal borrowed

    // What the same deal looks like WITHOUT rolling the negative equity
    // (i.e., if they paid off the gap separately before trading in)
    const principalClean  = Math.max(0, price - down - tradeInCredit);
    const paymentClean    = calcMonthlyPayment(principalClean, apr, months);
    const interestClean   = paymentClean * months - principalClean;
    const rolledExtraInt  = totalInterest - interestClean; // extra interest from rolled debt
    const rolledPaymentDelta = payment - paymentClean;     // extra per month from rolled debt

    // DTI check (gross income, contractual debt only — what lenders see)
    const allDebt = debt + payment;
    const dtiPct  = (allDebt / grossMo) * 100;
    const dtiOk   = dtiPct <= 36;

    // Real cash flow (take-home, all obligations — what life sees)
    // Cash flow uses new insurance premium (replaces current in monthly obligations)
    // If user hasn't entered new insurance, fall back to current insurance
    const insuranceInCashFlow = hasInsuranceData ? newInsurance : currentInsurance;
    const totalOut = living + debt + payment + insuranceInCashFlow;
    const trueMonthlyCost = payment + newInsurance; // payment + insurance = real monthly hit
    const leftover = takehome - totalOut;
    const cfOk  = leftover >= 500;
    const cfWarn = leftover >= 0 && leftover < 500;

    const downPct   = (down + tradeInCredit) / price;
    const transPct  = (payment / grossMo) * 100;
    const benchmark = benchmarkRaw || CREDIT_RATE_BENCHMARKS.bank[fin.credit];
    const rateOver  = apr - benchmark;
    // Manufacturer rates not available for poor credit — flag separately
    const mfrUnavailable = fin.financingType === "manufacturer" && benchmarkRaw === null;

    const downOk  = downPct >= 0.20;
    const termOk  = months <= 48;
    const transOk = transPct <= 10;
    const rateOk  = rateOver <= 2;

    // Savings check
    const bothZero        = savings === 0 && down === 0 && tradeIn === 0;
    const noDownButSavings = (down + tradeInCredit) === 0 && savings > 0;
    const savOk  = !bothZero && !noDownButSavings && savings >= (down + tradeInCredit) * 1.2;
    const savWarn = noDownButSavings;

    // Rolled negative equity check — hard fail if rolled amount > 10% of new car price
    const negEqOk   = rolledDebt === 0;
    const negEqWarn = rolledDebt > 0 && rolledDebt <= price * 0.10;
    const negEqFail = rolledDebt > price * 0.10;

    const fails = [!downOk, !termOk, !transOk, !dtiOk, !rateOk, (!savOk && !savWarn), negEqFail].filter(Boolean).length;
    const verdict = fails === 0 && cfOk && negEqOk ? "pass"
      : (fails <= 2 && leftover >= 0 && !negEqFail) ? "caution"
      : "fail";

    const loan = calcAmortization(principal, apr, months);
    const depr = calcDepreciationSchedule(price, months, car.deprProfile, car.isNew);
    // Count every underwater month across the full schedule (not just the front).
    // Also track the worst gap (max negative equity point).
    let uwMonths = 0;
    let maxGap = 0;       // largest negative equity value (loan - car value) across term
    for (let i = 0; i < months; i++) {
      const gap = loan[i] - depr[i]; // positive = underwater
      if (gap > 0) uwMonths++;
      if (gap > maxGap) maxGap = gap;
    }

    // Same for clean scenario
    const loanClean = calcAmortization(principalClean, apr, months);
    // deprSchedule already accounts for isNew — reuse same depr for clean comparison
    let uwMonthsClean = 0;
    let maxGapClean = 0;
    for (let i = 0; i < months; i++) {
      const gap = loanClean[i] - depr[i];
      if (gap > 0) uwMonthsClean++;
      if (gap > maxGapClean) maxGapClean = gap;
    }

    const cheapPrice   = price * 0.80;
    const cheapPayment = calcMonthlyPayment(Math.max(0, cheapPrice - down - tradeInCredit + rolledDebt), apr, months);
    const diff = payment - cheapPayment;
    let oppBal = 0;
    const r = 0.07 / 12;
    for (let i = 0; i < months; i++) oppBal = oppBal * (1 + r) + diff;

    // 36-month baseline comparison — same car, same APR, same down
    const baselineMonths = 36;
    const baselinePayment = months <= 36 ? null : calcMonthlyPayment(principal, apr, baselineMonths);
    const baselineTotalInterest = baselinePayment ? (baselinePayment * baselineMonths) - principal : null;
    const termExtraInterest = baselinePayment ? totalInterest - baselineTotalInterest : null;
    const termMonthlyDiff = baselinePayment ? baselinePayment - payment : null; // baseline costs more/mo
    // What investing the monthly savings (negative — baseline costs MORE per month) looks like
    // Actually: longer loan = lower payment but more total interest.
    // Show: extra interest paid vs 36mo, and what that sum would be worth invested.
    let termOppBal = 0;
    if (baselinePayment && termExtraInterest > 0) {
      // Treat the extra interest as a lump sum lost — simple illustration
      termOppBal = termExtraInterest * Math.pow(1 + r, baselineMonths);
    }

    return {
      payment, totalPaid, totalInterest, principal, apr, months,
      downPct, transPct, dtiPct, rateOver, benchmark, mfrUnavailable,
      downOk, termOk, transOk, dtiOk, rateOk, savOk, savWarn, bothZero,
      cfOk, cfWarn, leftover, takehome, taxRate,
      living, debt, grossMo,
      currentInsurance, newInsurance, insuranceDelta, hasInsuranceData, trueMonthlyCost, insuranceInCashFlow,
      verdict, fails,
      loan, depr, uwMonths, maxGap, deprProfile: car.deprProfile,
      loanClean, uwMonthsClean,
      diff, oppBal, cheapPayment, cheapPrice,
      baselineMonths, baselinePayment, baselineTotalInterest, termExtraInterest, termMonthlyDiff, termOppBal,
      rolledDebt, tradeInEquity, tradeInCredit, tradeInPayoff,
      negEqOk, negEqWarn, negEqFail,
      rolledExtraInt, rolledPaymentDelta, paymentClean,
    };
  }, [fin, car]);

  const vc = R?.verdict === "pass" ? C.green : R?.verdict === "caution" ? C.yellow : C.red;
  const vl = R?.verdict === "pass" ? "Looks Reasonable" : R?.verdict === "caution" ? "Proceed with Caution" : "Do Not Buy This Car";

  const inp = { background: "#0f1117", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: "9px 12px", fontSize: 15, width: "100%", boxSizing: "border-box", outline: "none", fontFamily: "inherit" };
  const lbl = { fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3, display: "block" };
  const sub = { fontSize: 16, color: "#64748b", marginBottom: 6, display: "block", lineHeight: 1.5 };
  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px" };

  const Row = ({ ok, warn, label, detail }) => (
    <div style={{ display: "flex", gap: 9, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 16, color: ok ? C.green : warn ? C.yellow : C.red, flexShrink: 0, marginTop: 1 }}>{ok ? "✓" : warn ? "⚠" : "✗"}</span>
      <div>
        <div style={{ fontSize: 16, color: ok ? C.green : warn ? C.yellow : C.red, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 16, color: C.muted, marginTop: 2 }}>{detail}</div>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif", paddingBottom: 60 }}>
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "15px 26px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>🚗</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.02em" }}>CarTruth</div>
          <div style={{ fontSize: 14, color: C.muted }}>Independent affordability calculator — no ads, no dealers, no conflicts of interest</div>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "26px 18px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

          {/* Financial Profile */}
          <div style={card}>
            <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, fontWeight: 600 }}>Your Financial Profile</div>
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label style={lbl}>Annual gross income</label>
                <span style={sub}>Your total salary before taxes are taken out</span>
                <input style={inp} type="number" placeholder="75000" value={fin.annualIncome} onChange={e => sf("annualIncome", e.target.value)} />
              </div>

              <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)", borderRadius: 9, padding: "11px 13px" }}>
                <label style={{ ...lbl, color: "#f87171" }}>Monthly debt payments</label>
                <span style={{ ...sub, color: "#5a3a3a" }}>Contractual obligations only — credit card minimums, student loans, other car loans, mortgage or rent. This is what lenders use for your DTI ratio.</span>
                <input style={inp} type="number" placeholder="400" value={fin.monthlyDebt} onChange={e => sf("monthlyDebt", e.target.value)} />
              </div>

              <div style={{ background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.18)", borderRadius: 9, padding: "11px 13px" }}>
                <label style={{ ...lbl, color: "#fb923c" }}>Monthly living expenses</label>
                <span style={{ ...sub, color: "#5a4030" }}>Everything else — groceries, utilities, gas, subscriptions, dining out. Do not include car insurance here — enter it separately below.</span>
                <input style={inp} type="number" placeholder="2200" value={fin.monthlyLiving} onChange={e => sf("monthlyLiving", e.target.value)} />
              </div>

              {/* Insurance section */}
              <div style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.18)", borderRadius: 9, padding: "12px 13px" }}>
                <div style={{ fontSize: 14, color: "#34d399", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>Car insurance</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
                  <div>
                    <label style={lbl}>Current monthly premium</label>
                    <span style={{ ...sub, minHeight: 40, display: "block" }}>What you pay now — enter 0 if no current car</span>
                    <input style={inp} type="number" placeholder="120" value={fin.currentInsurance} onChange={e => sf("currentInsurance", e.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>New car estimated premium</label>
                    <span style={{ ...sub, minHeight: 40, display: "block" }}>Get a quote — newer/pricier cars cost more to insure</span>
                    <input style={inp} type="number" placeholder="190" value={fin.newInsurance} onChange={e => sf("newInsurance", e.target.value)} />
                  </div>
                </div>
                {(+fin.currentInsurance > 0 || +fin.newInsurance > 0) && (() => {
                  const delta = (+fin.newInsurance || 0) - (+fin.currentInsurance || 0);
                  const isIncrease = delta > 0;
                  return (
                    <div style={{ marginTop: 8, padding: "7px 10px", borderRadius: 7, background: isIncrease ? "rgba(239,68,68,0.07)" : "rgba(34,197,94,0.07)", border: `1px solid ${isIncrease ? "rgba(239,68,68,0.2)" : "rgba(34,197,94,0.2)"}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 16, color: isIncrease ? C.red : C.green, fontWeight: 600 }}>
                        {isIncrease ? `⚠ Insurance increases by ${fmt(delta)}/mo` : delta < 0 ? `✓ Insurance decreases by ${fmt(Math.abs(delta))}/mo` : "✓ Insurance cost unchanged"}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: isIncrease ? C.red : C.green }}>{isIncrease ? "+" : ""}{fmt(delta)}/mo</div>
                    </div>
                  );
                })()}
                <div style={{ fontSize: 14, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                  Don't have a quote yet? Try Progressive, Geico, or your current insurer's website. New cars, luxury vehicles, and sports cars typically run 20–40% higher than economy sedans.
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={lbl}>Liquid savings</label>
                  <span style={sub}>Cash you can access now</span>
                  <input style={inp} type="number" placeholder="15000" value={fin.savings} onChange={e => sf("savings", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Credit score tier</label>
                  <span style={sub}>Used to set APR benchmark</span>
                  <select style={inp} value={fin.credit} onChange={e => { sf("credit", e.target.value); sc("apr", ""); }}>
                    <option value="excellent">Excellent (750+)</option>
                    <option value="good">Good (680–749)</option>
                    <option value="fair">Fair (580–679)</option>
                    <option value="poor">Poor (below 580)</option>
                  </select>
                </div>
              </div>

              {/* Financing type + benchmark display */}
              <div style={{ background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.18)", borderRadius: 9, padding: "12px 13px" }}>
                <div style={{ fontSize: 14, color: "#60a5fa", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>Financing source</div>
                <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
                  {(car.isNew
                    ? [["bank", "Bank / Credit Union"], ["manufacturer", "Manufacturer Incentive"]]
                    : [["bank", "Bank / Credit Union"]]
                  ).map(([key, label]) => (
                    <button key={key} onClick={() => { sf("financingType", key); sc("apr", ""); }} style={{ flex: 1, padding: "7px 6px", borderRadius: 7, border: `1px solid ${fin.financingType === key ? C.blue : C.border}`, background: fin.financingType === key ? "rgba(59,130,246,0.15)" : "transparent", color: fin.financingType === key ? C.blue : C.muted, fontSize: 16, cursor: "pointer", fontFamily: "inherit", fontWeight: fin.financingType === key ? 600 : 400, lineHeight: 1.3, textAlign: "center" }}>
                      {label}
                    </button>
                  ))}
                </div>
                {!car.isNew && (
                  <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
                    Manufacturer incentive rates are only available on new cars — bank/credit union rates shown.
                  </div>
                )}
                {car.isNew && fin.financingType === "manufacturer" && (
                  <div style={{ fontSize: 14, color: "#f59e0b", lineHeight: 1.5, marginBottom: 8 }}>
                    ⚠ Manufacturer rates apply to new cars only, on select models, and are typically only available to buyers with excellent credit.
                  </div>
                )}
                {/* Benchmark rate display — all four tiers visible */}
                <div style={{ fontSize: 14, color: C.muted, marginBottom: 5 }}>
                  Benchmark APR by credit tier <span style={{ color: "#3d4a5c" }}>(updated Mar 2026 — verify current rates at your bank or CFPB)</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 5 }}>
                  {[["excellent","750+"],["good","680–749"],["fair","580–679"],["poor","<580"]].map(([tier, range]) => {
                    const rate = CREDIT_RATE_BENCHMARKS[fin.financingType]?.[tier];
                    const isActive = fin.credit === tier;
                    return (
                      <div key={tier} onClick={() => { sf("credit", tier); sc("apr", ""); }} style={{ padding: "6px 4px", borderRadius: 6, border: `1px solid ${isActive ? C.blue : C.border}`, background: isActive ? "rgba(59,130,246,0.12)" : "rgba(0,0,0,0.2)", cursor: "pointer", textAlign: "center" }}>
                        <div style={{ fontSize: 16, color: isActive ? C.blue : C.muted, marginBottom: 2 }}>{range}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: rate === null ? C.muted : isActive ? C.blue : C.text }}>
                          {rate === null ? "N/A" : `${rate}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle & Loan */}
          <div style={card}>
            <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14, fontWeight: 600 }}>Vehicle &amp; Loan Details</div>
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", gap: 8 }}>
                {["New", "Used"].map(t => (
                  <button key={t} onClick={() => {
                    sc("isNew", t === "New");
                    // Used cars can't use manufacturer incentive financing
                    if (t === "Used" && fin.financingType === "manufacturer") {
                      sf("financingType", "bank");
                      sc("apr", "");
                    }
                  }} style={{ flex: 1, padding: "8px 0", borderRadius: 7, border: `1px solid ${car.isNew === (t === "New") ? C.blue : C.border}`, background: car.isNew === (t === "New") ? "rgba(59,130,246,0.15)" : "transparent", color: car.isNew === (t === "New") ? C.blue : C.muted, fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}>
                    {t}
                  </button>
                ))}
              </div>
              <div>
                <label style={lbl}>Depreciation profile</label>
                <span style={sub}>How fast does this type of vehicle lose value?</span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {Object.entries(DEPR_PROFILES).map(([key, prof]) => (
                    <button key={key} onClick={() => sc("deprProfile", key)} style={{ padding: "8px 6px", borderRadius: 7, border: `1px solid ${car.deprProfile === key ? (key === "strong" ? C.green : key === "average" ? C.blue : C.yellow) : C.border}`, background: car.deprProfile === key ? (key === "strong" ? "rgba(34,197,94,0.12)" : key === "average" ? "rgba(59,130,246,0.12)" : "rgba(245,158,11,0.12)") : "transparent", color: car.deprProfile === key ? (key === "strong" ? C.green : key === "average" ? C.blue : C.yellow) : C.muted, fontSize: 16, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3, textAlign: "center" }}>
                      <div style={{ fontWeight: 600 }}>{prof.label}</div>
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 14, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
                  {DEPR_PROFILES[car.deprProfile].examples}
                </div>
              </div>

              <div>
                <label style={lbl}>Vehicle price</label>
                <span style={sub}>Sticker or asking price — before you negotiate</span>
                <input style={inp} type="number" placeholder="35000" value={car.price} onChange={e => sc("price", e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Down payment (cash)</label>
                <span style={sub}>Cash you're putting down at signing</span>
                <input style={inp} type="number" placeholder="7000" value={car.down} onChange={e => sc("down", e.target.value)} />
              </div>

              {/* Trade-in section */}
              <div style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 9, padding: "12px 13px" }}>
                <div style={{ fontSize: 14, color: "#818cf8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>Trade-in (leave blank if no trade-in)</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={lbl}>Trade-in value</label>
                    <span style={sub}>What the dealer is offering for your car</span>
                    <input style={inp} type="number" placeholder="0" value={car.tradeIn} onChange={e => sc("tradeIn", e.target.value)} />
                  </div>
                  <div>
                    <label style={lbl}>Current loan payoff</label>
                    <span style={sub}>What you still owe on your current car</span>
                    <input style={inp} type="number" placeholder="0" value={car.tradeInPayoff} onChange={e => sc("tradeInPayoff", e.target.value)} />
                  </div>
                </div>
                {/* Equity gap display */}
                {(+car.tradeIn || +car.tradeInPayoff) ? (() => {
                  const eq = (+car.tradeIn || 0) - (+car.tradeInPayoff || 0);
                  const isNeg = eq < 0;
                  return (
                    <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 7, background: isNeg ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${isNeg ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: isNeg ? C.red : C.green }}>
                          {isNeg ? "⚠ Negative equity — will be rolled into new loan" : "✓ Positive equity — applied to new loan"}
                        </div>
                        <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>
                          {isNeg
                            ? `You owe ${fmt(Math.abs(eq))} more than the car is worth`
                            : `${fmt(eq)} will reduce what you need to finance`}
                        </div>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: isNeg ? C.red : C.green, marginLeft: 12 }}>
                        {isNeg ? "-" : "+"}{fmt(Math.abs(eq))}
                      </div>
                    </div>
                  );
                })() : null}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <label style={lbl}>APR offered (%)</label>
                    {car.apr
                      ? <button onClick={() => sc("apr", "")} style={{ fontSize: 14, color: C.blue, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline" }}>✕ clear — use benchmark</button>
                      : <span style={{ fontSize: 14, color: C.green }}>✓ using {CREDIT_RATE_BENCHMARKS[fin.financingType]?.[fin.credit] ?? CREDIT_RATE_BENCHMARKS.bank[fin.credit]}% benchmark</span>
                    }
                  </div>
                  <input style={{ ...inp, borderColor: car.apr ? C.blue : C.border }} type="number" placeholder={CREDIT_RATE_BENCHMARKS[fin.financingType]?.[fin.credit] ?? CREDIT_RATE_BENCHMARKS.bank[fin.credit]} value={car.apr} onChange={e => sc("apr", e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>Loan term</label>
                  <span style={sub}>Shorter = less interest paid overall</span>
                  <select style={inp} value={car.term} onChange={e => sc("term", e.target.value)}>
                    <option value="24">24 mo (2 yr)</option>
                    <option value="36">36 mo (3 yr)</option>
                    <option value="48">48 mo (4 yr)</option>
                    <option value="60">60 mo (5 yr)</option>
                    <option value="72">72 mo (6 yr)</option>
                    <option value="84">84 mo (7 yr)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {R ? (<>
          {/* Verdict Banner */}
          <div style={{ ...card, borderColor: vc, background: `rgba(${R.verdict === "pass" ? "34,197,94" : R.verdict === "caution" ? "245,158,11" : "239,68,68"},0.07)`, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px" }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: vc, letterSpacing: "-0.03em" }}>{vl}</div>
              <div style={{ fontSize: 16, color: C.muted, marginTop: 3 }}>
                {R.verdict === "pass" && (R.cfOk ? "Passes all standard rules and real cash flow check." : "Passes standard rules — but review cash flow below.")}
                {R.verdict === "caution" && `${R.fails} of 6 rules failed. Understand the risks before signing.`}
                {R.verdict === "fail" && `${R.fails} of 6 rules failed${R.leftover < 0 ? ", and cash flow goes negative" : ""}. The math says no.`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              {R.hasInsuranceData ? (<>
                <div style={{ fontSize: 34, fontWeight: 800, color: vc, letterSpacing: "-0.04em" }}>{fmt(R.trueMonthlyCost)}<span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>/mo true cost</span></div>
                <div style={{ fontSize: 16, color: C.muted }}>{fmt(R.payment)} payment + {fmt(R.newInsurance)} insurance</div>
                <div style={{ fontSize: 16, color: C.muted }}>{fmt(R.totalPaid)} total loan · {fmt(R.totalInterest)} interest</div>
              </>) : (<>
                <div style={{ fontSize: 34, fontWeight: 800, color: vc, letterSpacing: "-0.04em" }}>{fmt(R.payment)}<span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>/mo</span></div>
                <div style={{ fontSize: 16, color: C.muted }}>{fmt(R.totalPaid)} total · {fmt(R.totalInterest)} in interest</div>
              </>)}
            </div>
          </div>

          {/* Real Cash Flow Panel */}
          {(fin.monthlyLiving || fin.currentInsurance || fin.newInsurance) && (
            <div style={{ ...card, borderColor: R.cfOk ? C.green : R.cfWarn ? C.yellow : C.red, background: `rgba(${R.cfOk ? "34,197,94" : R.cfWarn ? "245,158,11" : "239,68,68"},0.05)`, marginBottom: 16, padding: "16px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 5 }}>
                    Real Cash Flow — What Your Bank Account Actually Sees
                  </div>
                  <div style={{ fontSize: 16, color: C.muted, lineHeight: 1.7, marginBottom: 8 }}>
                    Take-home <strong style={{ color: C.text }}>{fmt(R.takehome)}/mo</strong> (est. {pct(R.taxRate * 100)} tax rate)
                    &nbsp;−&nbsp;living expenses <strong style={{ color: C.text }}>{fmt(R.living)}</strong>
                    &nbsp;−&nbsp;existing debt <strong style={{ color: C.text }}>{fmt(R.debt)}</strong>
                    &nbsp;−&nbsp;car payment <strong style={{ color: C.text }}>{fmt(R.payment)}</strong>
                    {R.insuranceInCashFlow > 0 && <>&nbsp;−&nbsp;insurance <strong style={{ color: R.insuranceDelta > 0 ? C.red : C.text }}>{fmt(R.insuranceInCashFlow)}{R.hasInsuranceData && R.insuranceDelta > 0 ? ` (+${fmt(R.insuranceDelta)} vs. current)` : ""}</strong></>}
                  </div>
                  <CashFlowBar leftover={R.leftover} takeHome={R.takehome} />
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: R.cfOk ? C.green : R.cfWarn ? C.yellow : C.red, letterSpacing: "-0.03em" }}>{fmt(R.leftover)}</div>
                  <div style={{ fontSize: 16, color: C.muted }}>left each month</div>
                  {R.cfWarn && <div style={{ fontSize: 16, color: C.yellow, marginTop: 4, fontWeight: 600 }}>⚠ Under $500 buffer</div>}
                  {!R.cfOk && !R.cfWarn && <div style={{ fontSize: 16, color: C.red, marginTop: 4, fontWeight: 600 }}>⚠ Negative cash flow</div>}
                </div>
              </div>
            </div>
          )}

          {/* Rolled Negative Equity Warning Panel */}
          {R.rolledDebt > 0 && (
            <div style={{ ...card, borderColor: R.negEqFail ? C.red : C.yellow, background: R.negEqFail ? "rgba(239,68,68,0.06)" : "rgba(245,158,11,0.06)", marginBottom: 16, padding: "16px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 6, color: R.negEqFail ? C.red : C.yellow }}>
                    {R.negEqFail ? "⚠ Danger: Rolling Significant Negative Equity" : "⚠ Warning: Rolling Negative Equity Into New Loan"}
                  </div>
                  <div style={{ fontSize: 16, color: C.muted, lineHeight: 1.8 }}>
                    <span style={{ color: C.text, fontWeight: 600 }}>{fmt(R.rolledDebt)}</span> of debt from your current car is being added to this loan.
                    You will pay interest on a vehicle you no longer own for the full {R.months}-month term.<br />
                    This adds <span style={{ color: C.red, fontWeight: 600 }}>{fmt(R.rolledExtraInt)}</span> in extra interest and increases your monthly payment by <span style={{ color: C.red, fontWeight: 600 }}>{fmt(R.rolledPaymentDelta)}/mo</span>.<br />
                    {R.negEqFail && <span style={{ color: C.red }}>Rolled debt exceeds 10% of the new car's price — if this car is totaled, you could be {fmt(R.rolledDebt)}+ out of pocket after insurance pays out.</span>}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 16, color: C.muted }}>
                    Without rolling this debt, your payment would be <strong style={{ color: C.text }}>{fmt(R.paymentClean)}/mo</strong> and you'd be underwater for <strong style={{ color: C.text }}>{R.uwMonthsClean} months</strong> instead of {R.uwMonths}.
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, color: C.muted, marginBottom: 2 }}>Ghost debt on new loan</div>
                  <div style={{ fontSize: 34, fontWeight: 800, color: R.negEqFail ? C.red : C.yellow, letterSpacing: "-0.03em" }}>{fmt(R.rolledDebt)}</div>
                  <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>{pct(R.rolledDebt / (+car.price || 1) * 100)} of new car's price</div>
                </div>
              </div>
            </div>
          )}

          {/* Three modules */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>

            {/* Rule Checks */}
            <div style={card}>
              <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, fontWeight: 600 }}>Standard Rule Checks</div>
              <Row ok={R.downOk} label={`Down: ${pct(R.downPct * 100)}`} detail="Minimum 20% (cash + trade-in)" />
              <Row ok={R.termOk} label={`Term: ${car.term} months`} detail="Maximum 48 months recommended" />
              <Row ok={R.transOk} label={`Transport: ${pct(R.transPct)} of gross`} detail="Payment should be ≤10% of monthly income" />
              <Row ok={R.dtiOk} label={`DTI: ${pct(R.dtiPct)}`} detail="All debt payments ≤36% of gross income" />
              {R.mfrUnavailable
                ? <Row ok={false} label={`APR: ${pct(R.apr)}`} detail="Manufacturer incentive rates not available for this credit tier — using bank benchmark" />
                : <Row ok={R.rateOk} label={`APR: ${pct(R.apr)}`} detail={`${fin.financingType === "manufacturer" ? "Mfr incentive" : "Bank/CU"} benchmark for your tier: ${pct(R.benchmark)} — ${R.rateOver > 0 ? `${pct(R.rateOver)} above benchmark` : "at or below benchmark"}`} />
              }
              <Row ok={R.savOk} warn={R.savWarn} label={`Savings: ${fmt(+fin.savings || 0)}`} detail={R.bothZero ? "No down payment and no savings — extremely high risk" : R.savWarn ? "No down payment — you have no equity from day one" : "Liquid savings should exceed 120% of down payment"} />
              {R.rolledDebt > 0 && <Row ok={R.negEqOk} warn={R.negEqWarn} label={`Rolled negative equity: ${fmt(R.rolledDebt)}`} detail={R.negEqFail ? "Exceeds 10% of new car price — extremely dangerous" : "Carrying debt from old car into new loan"} />}
              {(fin.monthlyLiving || fin.newInsurance) && <Row ok={R.cfOk} warn={R.cfWarn} label={`Cash flow: ${fmt(R.leftover)}/mo`} detail="After taxes, all expenses, insurance and debt — need $500+ buffer" />}
            </div>

            {/* Equity Timeline */}
            <div style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 16, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>Equity Timeline</div>
                <div style={{ fontSize: 14, color: R.deprProfile === "strong" ? C.green : R.deprProfile === "average" ? C.blue : C.yellow, background: R.deprProfile === "strong" ? "rgba(34,197,94,0.1)" : R.deprProfile === "average" ? "rgba(59,130,246,0.1)" : "rgba(245,158,11,0.1)", padding: "2px 8px", borderRadius: 10 }}>
                  {DEPR_PROFILES[R.deprProfile].label}
                </div>
              </div>
              {(() => {
                const allUnder = R.uwMonths === R.months;
                const noneUnder = R.uwMonths === 0;
                // Red if underwater >= 20% of loan duration, green otherwise
                const color = noneUnder ? C.green : (R.uwMonths / R.months) >= 0.20 ? C.red : C.green;
                return (
                  <div style={{ marginBottom: 8 }}>
                    {noneUnder ? (
                      <div style={{ fontSize: 16, color: C.green, fontWeight: 700 }}>
                        No underwater months — positive equity throughout the full {R.months}-month loan ✓
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: 16, fontWeight: 700, color, lineHeight: 1.5 }}>
                          Underwater for <span style={{ fontSize: 20 }}>{R.uwMonths}</span> of {R.months} months
                          {R.maxGap > 0 && <>, peaking at <span style={{ fontSize: 20 }}>{new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(R.maxGap)}</span> negative</>}
                        </div>
                        <div style={{ fontSize: 16, color: C.muted, marginTop: 3 }}>
                          {allUnder
                            ? "Car never reaches positive equity within this loan — you owe more than it's worth the entire term"
                            : `Car value below loan balance for ${(R.uwMonths / 12).toFixed(1)} yrs of this loan — if totaled during that window, you'd still owe money`}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              <EquityChart loanSchedule={R.loan} deprSchedule={R.depr} months={R.months} />
              <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 14, height: 2, background: C.blue }} /><span style={{ fontSize: 14, color: C.muted }}>Loan balance</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 14, height: 2, background: C.yellow, borderTop: "2px dashed " + C.yellow }} /><span style={{ fontSize: 14, color: C.muted }}>Car value</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }} /><span style={{ fontSize: 14, color: C.muted }}>Breakeven</span></div>
              </div>
              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 12, paddingTop: 10 }}>
                <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 4 }}>Principal vs. Interest Breakdown</div>
                <PrincipalVsInterestBar principal={R.principal} totalInterest={R.totalInterest} totalPaid={R.totalPaid} />
              </div>
            </div>

            {/* Opportunity Cost */}
            <div style={card}>
              <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, fontWeight: 600 }}>Opportunity Cost</div>

              {/* Section A: cheaper car */}
              <div style={{ fontSize: 14, color: C.muted, marginBottom: 4, lineHeight: 1.5 }}>
                20% cheaper car ({fmt(R.cheapPrice)}) — invest the {fmt(R.diff)}/mo difference at 7%:
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.purple, letterSpacing: "-0.03em" }}>{fmt(R.oppBal)}</div>
              <div style={{ fontSize: 14, color: C.muted, marginTop: 1, marginBottom: 6 }}>after {(R.months / 12).toFixed(0)} years</div>
              <OppCostChart monthlyDiff={R.diff} months={R.months} />
              <div style={{ marginTop: 8, background: "rgba(139,92,246,0.08)", borderRadius: 7, padding: "8px 10px", display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 14, color: C.muted }}>Cheaper car payment</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.purple }}>{fmt(R.cheapPayment)}<span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>/mo</span></div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, color: C.muted }}>You'd save monthly</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.purple }}>{fmt(R.diff)}</div>
                </div>
              </div>

              {/* Section B: term length comparison — only show if loan > 36 months */}
              {R.baselinePayment && R.termExtraInterest > 0 && (
                <>
                  <div style={{ borderTop: `1px solid ${C.border}`, margin: "14px 0 10px" }} />
                  <div style={{ fontSize: 14, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 8 }}>
                    Cost of a longer loan vs. 36-month baseline
                  </div>

                  {/* Term comparison stat row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                    <div style={{ background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 7, padding: "8px 10px" }}>
                      <div style={{ fontSize: 14, color: C.muted, marginBottom: 2 }}>36-month payment</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.green }}>{fmt(R.baselinePayment)}<span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>/mo</span></div>
                      <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>Interest: {fmt(R.baselineTotalInterest)}</div>
                    </div>
                    <div style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 7, padding: "8px 10px" }}>
                      <div style={{ fontSize: 14, color: C.muted, marginBottom: 2 }}>{R.months}-month payment</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.red }}>{fmt(R.payment)}<span style={{ fontSize: 14, fontWeight: 400, color: C.muted }}>/mo</span></div>
                      <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>Interest: {fmt(R.totalInterest)}</div>
                    </div>
                  </div>

                  {/* The gut-punch number */}
                  <div style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.22)", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 16, color: C.red, fontWeight: 700, marginBottom: 3 }}>
                      Choosing {R.months} months over 36 costs you {fmt(R.termExtraInterest)} in extra interest
                    </div>
                    <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                      Yes, your payment drops by {fmt(Math.abs(R.termMonthlyDiff))}/mo — but you pay {fmt(R.termExtraInterest)} more to the bank over the life of the loan.
                      Invested at 7%, that {fmt(R.termExtraInterest)} would grow to <span style={{ color: C.yellow, fontWeight: 600 }}>{fmt(R.termOppBal)}</span>.
                    </div>
                  </div>
                </>
              )}

              {R.months <= 36 && (
                <div style={{ marginTop: 10, fontSize: 16, color: C.green, fontWeight: 600 }}>
                  ✓ You're already at or under the 36-month baseline — good discipline on term length.
                </div>
              )}
            </div>
          </div>

          <div style={{ textAlign: "center", fontSize: 16, color: C.muted, lineHeight: 1.7 }}>
            Educational purposes only — not financial advice. Tax estimate is approximate. Always consult a qualified financial advisor before major purchases.
          </div>
        </>) : (
          <div style={{ textAlign: "center", padding: "48px 0", color: C.muted }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🧮</div>
            <div style={{ fontSize: 15 }}>Enter your income and a vehicle price above to see your full analysis</div>
          </div>
        )}
      </div>
    </div>
  );
}
